import { useEffect, useMemo, useRef, useState } from 'react'
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom'
import CenterPopup from '../templates/ui/CenterPopup'
import { useCampaignStore } from '../app/store/campaignStore'
import ReviewResult from './ReviewResult'

const RECEIPT_KEYS = ['receiptNo', 'receipt_no', 'receipt', 'ref']
const PARTNER_KEYS = ['partnerCode', 'partner_code', 'partnerId', 'partner_id', 'partner']
const CUSTOMER_KEYS = ['customer', 'customerId', 'customer_id', 'cus_id', 'cusId']
const REQUIRED_SCAN_QUERY_KEYS = ['customer', 'receiptNo', 'partnerId']
const PENDING_REWARD_POPUP_KEY = 'mutelu:pending-reward-popup'
const PENDING_DUPLICATE_RECEIPT_POPUP_KEY = 'mutelu:pending-duplicate-receipt-popup'
const PENDING_ERROR_POPUP_KEY = 'mutelu:pending-error-popup'
const PENDING_REWARD_POPUP_KEY_FALLBACK = 'mutelu:pending-reward-popup:fallback'
const PENDING_DUPLICATE_RECEIPT_POPUP_KEY_FALLBACK = 'mutelu:pending-duplicate-receipt-popup:fallback'
const PENDING_ERROR_POPUP_KEY_FALLBACK = 'mutelu:pending-error-popup:fallback'
const INVALID_RECEIPT_REDIRECT_MS = 1500

const pickFirstParam = (searchParams, keys) => {
  for (const key of keys) {
    const value = searchParams.get(key)
    if (typeof value === 'string' && value.trim()) {
      return value.trim()
    }
  }
  return ''
}

const readPendingPopupPayload = (primaryKey, fallbackKey) => {
  const fromSession = sessionStorage.getItem(primaryKey)
  const fromLocal = localStorage.getItem(fallbackKey)
  return fromSession || fromLocal || ''
}

const clearPendingPopupPayload = (primaryKey, fallbackKey) => {
  sessionStorage.removeItem(primaryKey)
  localStorage.removeItem(fallbackKey)
}

export default function Receipt() {
  const navigate = useNavigate()
  const location = useLocation()
  const { state } = useCampaignStore()
  const [searchParams] = useSearchParams()
  const [invalidReceiptPopupOpen, setInvalidReceiptPopupOpen] = useState(false)
  const [scanRewardPopupTemplates, setScanRewardPopupTemplates] = useState([])
  const [scanRewardPopupIndex, setScanRewardPopupIndex] = useState(0)
  const [scanRewardPopupOpen, setScanRewardPopupOpen] = useState(false)
  const [duplicateReceiptPopupTemplate, setDuplicateReceiptPopupTemplate] = useState(null)
  const [duplicateReceiptPopupOpen, setDuplicateReceiptPopupOpen] = useState(false)
  const [errorPopupTemplate, setErrorPopupTemplate] = useState(null)
  const [errorPopupOpen, setErrorPopupOpen] = useState(false)
  const [invalidLandingPopupOpen, setInvalidLandingPopupOpen] = useState(false)
  const rewardPopupTimerRef = useRef(null)
  const errorPopupTimerRef = useRef(null)
  const invalidPopupTimerRef = useRef(null)
  const hasQueryParams = useMemo(() => [...searchParams.keys()].length > 0, [searchParams])

  useEffect(() => {
    if (hasQueryParams) return
    const stateReceiptNo =
      String(
        state?.fortuneResult?.receipt_no ||
          state?.fortuneResult?.receiptNo ||
          state?.receiptNo ||
          ''
      ).trim()
    if (stateReceiptNo) return
    setInvalidLandingPopupOpen(true)
  }, [hasQueryParams, state?.fortuneResult, state?.receiptNo])

  useEffect(() => {
    if (!hasQueryParams) return
    const rawQuery = Object.fromEntries(searchParams.entries())
    const customer = String(searchParams.get('customer') || '').trim()
    const receiptNo = String(searchParams.get('receiptNo') || '').trim()
    const partnerId = String(searchParams.get('partnerId') || '').trim()

    const missingKeys = REQUIRED_SCAN_QUERY_KEYS.filter((key) => {
      if (key === 'customer') return !customer
      if (key === 'receiptNo') return !receiptNo
      if (key === 'partnerId') return !partnerId
      return false
    })

    const parsedReceiptNo = pickFirstParam(searchParams, RECEIPT_KEYS)
    const parsedPartnerCode = pickFirstParam(searchParams, PARTNER_KEYS)
    const parsedCustomer = pickFirstParam(searchParams, CUSTOMER_KEYS)

    if (missingKeys.length > 0) {
      console.log('[Mutelu][Receipt] invalid query for submit-receipt redirect', {
        rawQuery,
        parsedReceiptNo,
        parsedPartnerCode,
        parsedCustomer,
        missingKeys
      })
      setInvalidReceiptPopupOpen(true)
      return
    }

    navigate(`/game/mutelu/submit-receipt?${searchParams.toString()}`, { replace: true })
  }, [hasQueryParams, navigate, searchParams])

  useEffect(() => {
    const statePayload = location.state?.pendingRewardPopup
    const raw =
      statePayload && typeof statePayload === 'object'
        ? JSON.stringify(statePayload)
        : readPendingPopupPayload(PENDING_REWARD_POPUP_KEY, PENDING_REWARD_POPUP_KEY_FALLBACK)
    if (!raw) return
    try {
      const parsed = JSON.parse(raw)
      console.log('[Mutelu][Receipt] pending reward popup raw parsed', parsed)
      const rewardList = Array.isArray(parsed) ? parsed : [parsed]
      const templates = rewardList
        .map((item) => {
          const type = String(item?.ticketType || '').trim().toLowerCase()
          const amount = Number(item?.amount || 0)
          if (!type || !Number.isFinite(amount) || amount <= 0) return null
          const ticketNameMap = {
            gold: 'ทอง',
            silver: 'เงิน',
            bronze: 'ทองแดง'
          }
          const ticketName = ticketNameMap[type] || type
          return {
            icon: '✅',
            title: `รับตั๋ว${ticketName} ${amount} ใบ`,
            subtitle: 'รับรางวัลจากการสแกนบิล',
            message: 'ระบบบันทึกรางวัลเรียบร้อยแล้ว กดดูคำทำนายได้เลย',
            confirmLabel: 'ดูคำทำนาย',
            cancelLabel: ''
          }
        })
        .filter(Boolean)

      if (templates.length === 0) {
        sessionStorage.removeItem(PENDING_REWARD_POPUP_KEY)
        return
      }

      setScanRewardPopupTemplates(templates)
      setScanRewardPopupIndex(0)
      setScanRewardPopupOpen(true)
    } catch (error) {
      console.log('[Mutelu][Receipt] parse pending reward popup failed', error)
    } finally {
      clearPendingPopupPayload(PENDING_REWARD_POPUP_KEY, PENDING_REWARD_POPUP_KEY_FALLBACK)
    }
  }, [location.state])

  useEffect(() => {
    const statePayload = location.state?.pendingErrorPopup
    const raw =
      statePayload && typeof statePayload === 'object'
        ? JSON.stringify(statePayload)
        : readPendingPopupPayload(PENDING_ERROR_POPUP_KEY, PENDING_ERROR_POPUP_KEY_FALLBACK)
    if (!raw) return
    try {
      const parsed = JSON.parse(raw)
      console.log('[Mutelu][Receipt] pending error popup raw parsed', parsed)
      setErrorPopupTemplate({
        icon: '⚠️',
        title: parsed?.title || 'เกิดข้อผิดพลาด',
        subtitle: parsed?.subtitle || 'ระบบขัดข้องชั่วคราว',
        message: parsed?.message || 'กรุณาลองใหม่อีกครั้ง',
        confirmLabel: '',
        cancelLabel: ''
      })
      setErrorPopupOpen(true)
    } catch (error) {
      console.log('[Mutelu][Receipt] parse pending error popup failed', error)
    } finally {
      clearPendingPopupPayload(PENDING_ERROR_POPUP_KEY, PENDING_ERROR_POPUP_KEY_FALLBACK)
    }
  }, [location.state])

  useEffect(() => {
    return () => {
      if (rewardPopupTimerRef.current) {
        window.clearTimeout(rewardPopupTimerRef.current)
        rewardPopupTimerRef.current = null
      }
      if (invalidPopupTimerRef.current) {
        window.clearTimeout(invalidPopupTimerRef.current)
        invalidPopupTimerRef.current = null
      }
      if (errorPopupTimerRef.current) {
        window.clearTimeout(errorPopupTimerRef.current)
        errorPopupTimerRef.current = null
      }
    }
  }, [])

  useEffect(() => {
    const shouldRedirect = invalidReceiptPopupOpen || invalidLandingPopupOpen
    if (!shouldRedirect) return
    if (invalidPopupTimerRef.current) {
      window.clearTimeout(invalidPopupTimerRef.current)
      invalidPopupTimerRef.current = null
    }
    invalidPopupTimerRef.current = window.setTimeout(() => {
      setInvalidReceiptPopupOpen(false)
      setInvalidLandingPopupOpen(false)
      navigate('/game/mutelu/activity', { replace: true })
    }, INVALID_RECEIPT_REDIRECT_MS)
    return () => {
      if (invalidPopupTimerRef.current) {
        window.clearTimeout(invalidPopupTimerRef.current)
        invalidPopupTimerRef.current = null
      }
    }
  }, [invalidLandingPopupOpen, invalidReceiptPopupOpen, navigate])

  useEffect(() => {
    if (!errorPopupOpen) return
    if (errorPopupTimerRef.current) {
      window.clearTimeout(errorPopupTimerRef.current)
      errorPopupTimerRef.current = null
    }
    errorPopupTimerRef.current = window.setTimeout(() => {
      setErrorPopupOpen(false)
      navigate('/game/mutelu/activity', { replace: true })
    }, 4000)
    return () => {
      if (errorPopupTimerRef.current) {
        window.clearTimeout(errorPopupTimerRef.current)
        errorPopupTimerRef.current = null
      }
    }
  }, [errorPopupOpen, navigate])

  useEffect(() => {
    const statePayload = location.state?.pendingDuplicatePopup
    const raw =
      statePayload && typeof statePayload === 'object'
        ? JSON.stringify(statePayload)
        : readPendingPopupPayload(
            PENDING_DUPLICATE_RECEIPT_POPUP_KEY,
            PENDING_DUPLICATE_RECEIPT_POPUP_KEY_FALLBACK
          )
    if (!raw) return
    try {
      const parsed = JSON.parse(raw)
      console.log('[Mutelu][Receipt] pending duplicate popup raw parsed', parsed)
      const isDailyCustomerLimit = String(parsed?.kind || '').trim() === 'daily_customer_limit'
      setDuplicateReceiptPopupTemplate({
        icon: 'ℹ️',
        title: isDailyCustomerLimit
          ? parsed?.title || 'คุณใช้สิทธิ์สแกนวันนี้ครบแล้ว'
          : parsed?.title || 'บิลนี้ถูกสแกนไปแล้ว',
        subtitle: '',
        message:
          parsed?.message ||
          (isDailyCustomerLimit
            ? 'สามารถกลับมาเล่นได้อีกครั้งในวันพรุ่งนี้'
            : 'คุณสามารถดูคำทำนายเดิมได้ แต่จะไม่ได้รับตั๋วเพิ่ม'),
        confirmLabel: isDailyCustomerLimit ? 'ตกลง' : 'ดูคำทำนาย',
        cancelLabel: '',
        redirectToActivity: isDailyCustomerLimit
      })
      setDuplicateReceiptPopupOpen(true)
    } catch (error) {
      console.log('[Mutelu][Receipt] parse duplicate receipt popup failed', error)
    } finally {
      clearPendingPopupPayload(
        PENDING_DUPLICATE_RECEIPT_POPUP_KEY,
        PENDING_DUPLICATE_RECEIPT_POPUP_KEY_FALLBACK
      )
    }
  }, [location.state])

  const invalidReceiptPopupTemplate = useMemo(
    () => ({
      icon: '⚠️',
      title: 'รูปแบบลิงก์ไม่ถูกต้อง',
      subtitle: 'ลิงก์สแกนไม่ครบ',
      message: 'ลิงก์ต้องมี customer, receiptNo และ partnerId',
      confirmLabel: '',
      cancelLabel: ''
    }),
    []
  )

  const invalidLandingPopupTemplate = useMemo(
    () => ({
      icon: '⚠️',
      title: 'ไม่พบพารามิเตอร์สแกน',
      subtitle: 'กรุณาเปิดผ่านลิงก์จาก QR',
      message: '',
      confirmLabel: '',
      cancelLabel: ''
    }),
    []
  )

  const handleCloseInvalidPopup = () => {
    setInvalidReceiptPopupOpen(false)
    navigate('/game/mutelu/activity', { replace: true })
  }

  const handleCloseInvalidLandingPopup = () => {
    setInvalidLandingPopupOpen(false)
    navigate('/game/mutelu/activity', { replace: true })
  }

  const handleCloseRewardPopup = () => {
    const hasNext = scanRewardPopupIndex < scanRewardPopupTemplates.length - 1
    setScanRewardPopupOpen(false)
    if (!hasNext) return

    if (rewardPopupTimerRef.current) {
      window.clearTimeout(rewardPopupTimerRef.current)
      rewardPopupTimerRef.current = null
    }
    rewardPopupTimerRef.current = window.setTimeout(() => {
      setScanRewardPopupIndex((prev) => prev + 1)
      setScanRewardPopupOpen(true)
    }, 2000)
  }

  const currentScanRewardPopupTemplate =
    scanRewardPopupTemplates[scanRewardPopupIndex] || null

  return (
    <>
      {!hasQueryParams ? <ReviewResult /> : null}
      <CenterPopup
        open={invalidReceiptPopupOpen}
        template={invalidReceiptPopupTemplate}
        onConfirm={handleCloseInvalidPopup}
        onCancel={handleCloseInvalidPopup}
      />
      <CenterPopup
        open={invalidLandingPopupOpen}
        template={invalidLandingPopupTemplate}
        onConfirm={handleCloseInvalidLandingPopup}
        onCancel={handleCloseInvalidLandingPopup}
      />
      <CenterPopup
        open={scanRewardPopupOpen}
        template={currentScanRewardPopupTemplate}
        onConfirm={handleCloseRewardPopup}
        onCancel={handleCloseRewardPopup}
      />
      <CenterPopup
        open={duplicateReceiptPopupOpen}
        template={duplicateReceiptPopupTemplate}
        onConfirm={() => {
          const shouldRedirect = Boolean(duplicateReceiptPopupTemplate?.redirectToActivity)
          setDuplicateReceiptPopupOpen(false)
          if (shouldRedirect) {
            navigate('/game/mutelu/activity', { replace: true })
          }
        }}
        onCancel={() => {
          const shouldRedirect = Boolean(duplicateReceiptPopupTemplate?.redirectToActivity)
          setDuplicateReceiptPopupOpen(false)
          if (shouldRedirect) {
            navigate('/game/mutelu/activity', { replace: true })
          }
        }}
      />
      <CenterPopup
        open={errorPopupOpen}
        template={errorPopupTemplate}
        onConfirm={() => setErrorPopupOpen(false)}
        onCancel={() => setErrorPopupOpen(false)}
      />
    </>
  )
}
